import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { useState, useRef, useEffect, useCallback } from "react";
import {
    Table,
    Button,
    Dropdown,
    Badge,
    Space,
    Tag,
    Input,
    Spin,
    Select,
} from "antd";
import {
    UploadOutlined,
    FileExcelOutlined,
    PlusOutlined,
    MoreOutlined,
    PrinterOutlined,
    EditOutlined,
    SearchOutlined,
    UserOutlined,
    ClockCircleOutlined,
} from "@ant-design/icons";

import { message } from "antd";

import Layout from '../../Layout'
// import ChartBlock from './ChartBlock' // tạm ẩn khối biểu đồ thống kê
import ScheduleDateCell from '../../components/ScheduleDateCell'

import * as XLSX from "xlsx";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import { useNavigate } from "react-router-dom";
import { LayoutGrid, Table2 } from "lucide-react";
import Calendar from './Calendar'
import { getAppointments, getAppointmentStatusSummary, AppointmentItem } from "../../api/Appointment";
import { useDebounce } from "../../hooks/useDebounce";
import useTableColumnSettings from "../../hooks/useTableColumnSettings";
import { ColumnSettingButton } from "../../components/TableColumnSetting";
import { mixGetV1, mixPutV1 } from "../../services/mixin";
import useAuthStore from "../../store/authStore";
import { getScheduledRange, isScheduledInRange } from "../../utils/scheduledRange";

dayjs.extend(isoWeek);

/** Nạp hết lịch hẹn để lọc theo ngày hẹn ở giao diện (API chưa lọc được theo scheduled_at) */
const SWEEP_PAGE_SIZE = 200;
const SWEEP_MAX_PAGES = 10;

/** Nhớ chế độ xem đã chọn để tải lại trang không nhảy về mặc định. */
const VIEW_STORAGE_KEY = "appointment_view_mode";

const readSavedView = (): string => {
    try {
        const saved = localStorage.getItem(VIEW_STORAGE_KEY);
        return saved === "pipeline" || saved === "table" ? saved : "table";
    } catch {
        return "table";
    }
};

export default function OrdersPage() {
    const { t } = useTranslation();
    const { isSuperAdmin } = useAuthStore();
    const isAdmin = isSuperAdmin;
    const [activeTab, setActiveTab] = useState("new");
    const [view, setView] = useState(readSavedView);

    const handleViewChange = (newView: string) => {
        setView(newView);
        try {
            localStorage.setItem(VIEW_STORAGE_KEY, newView);
        } catch {
            // localStorage bị chặn (chế độ ẩn danh...) -> vẫn đổi view, chỉ không nhớ được
        }
    };
    const fileInputRef = useRef<HTMLInputElement>(null);
    const navigate = useNavigate();

    // Appointment data states
    const [data, setData] = useState<AppointmentItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [keyword, setKeyword] = useState("");
    const debouncedKeyword = useDebounce(keyword, 400);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
    const [staffOptions, setStaffOptions] = useState<Array<{ label: string; value: number }>>([]);
    const [loadingStaffOptions, setLoadingStaffOptions] = useState(false);
    const [updatingStaffIds, setUpdatingStaffIds] = useState<number[]>([]);
    // Bộ lọc: người phụ trách (telesale) + theo ngày
    const [telesaleOptions, setTelesaleOptions] = useState<Array<{ label: string; value: number }>>([]);
    const [selectedTelesaleId, setSelectedTelesaleId] = useState<number | undefined>(undefined);
    const [datePeriod, setDatePeriod] = useState<string | undefined>(undefined);
    const [statusSummary, setStatusSummary] = useState<Record<string, number>>({
        all: 0,
        new: 0,
        confirmed: 0,
        pending: 0,
        picked: 0,
        cancel: 0,
    });

    const statusMap: Record<string, number | undefined> = {
        all: undefined,
        new: 1,
        confirmed: 2,
        pending: 3,
        picked: 4,
        cancel: 0,
    };

    const statusKeyMap: Record<string, string> = {
        all: "all",
        1: "new",
        2: "confirmed",
        3: "pending",
        4: "picked",
        0: "cancel",
    };

    /** Nạp toàn bộ lịch hẹn khớp các bộ lọc không phải trạng thái, để lọc theo ngày hẹn */
    const fetchAllForRange = useCallback(async () => {
        const collected: AppointmentItem[] = [];
        let page = 1;
        let total = 0;
        while (page <= SWEEP_MAX_PAGES) {
            const res = await getAppointments({
                page,
                take: SWEEP_PAGE_SIZE,
                search: debouncedKeyword || undefined,
                telesale_id: selectedTelesaleId,
            });
            if (!res.status) break;
            const items = res.data?.items || [];
            collected.push(...items);
            total = res.data?.total || collected.length;
            if (!items.length || collected.length >= total) break;
            page += 1;
        }
        return collected;
    }, [debouncedKeyword, selectedTelesaleId]);

    const fetchData = useCallback(async (page = 1) => {
        setLoading(true);
        try {
            const range = getScheduledRange(datePeriod);

            if (!range) {
                const res = await getAppointments({
                    page,
                    take: pagination.pageSize,
                    search: debouncedKeyword || undefined,
                    status: statusMap[activeTab],
                    telesale_id: selectedTelesaleId,
                });
                if (res.status) {
                    setData(res.data?.items || []);
                    setPagination(prev => ({
                        ...prev,
                        current: res.data?.current_page || 1,
                        total: res.data?.total || 0,
                    }));
                }
                return;
            }

            const inRange = (await fetchAllForRange()).filter(item => isScheduledInRange(item.scheduled_at, range));

            // Đếm theo trạng thái ngay trên tập đã lọc để số trên tab khớp danh sách
            const summary: Record<string, number> = {
                all: inRange.length,
                new: 0,
                confirmed: 0,
                pending: 0,
                picked: 0,
                cancel: 0,
            };
            // API lọc `status` theo thẻ trạng thái khách (customer_tag_status), không
            // theo cột `status` của lịch hẹn -> đếm và lọc ở đây phải dùng cùng trường
            // thì số trên tab mới khớp danh sách và khớp bảng lịch.
            const effectiveStatus = (item: AppointmentItem) =>
                Number(item.customer_tag_status ?? item.status);

            inRange.forEach(item => {
                const key = statusKeyMap[String(effectiveStatus(item))];
                if (key && key !== "all") summary[key] += 1;
            });
            setStatusSummary(summary);

            const wantedStatus = statusMap[activeTab];
            const filtered = wantedStatus === undefined
                ? inRange
                : inRange.filter(item => effectiveStatus(item) === wantedStatus);

            const size = pagination.pageSize;
            setData(filtered.slice((page - 1) * size, page * size));
            setPagination(prev => ({ ...prev, current: page, total: filtered.length }));
        } finally {
            setLoading(false);
        }
    }, [debouncedKeyword, activeTab, selectedTelesaleId, datePeriod, fetchAllForRange]);

    useEffect(() => {
        fetchData(1);
    }, [fetchData]);

    const fetchStatusSummary = useCallback(async () => {
        // Khi lọc theo ngày hẹn, số đếm được tính ngay trong fetchData (API summary
        // chỉ lọc được theo ngày tạo nên sẽ lệch với danh sách)
        if (getScheduledRange(datePeriod)) return;
        try {
            const res = await getAppointmentStatusSummary({
                telesale_id: selectedTelesaleId,
                search: debouncedKeyword || undefined,
            });
            if (!res.status) return;

            const next: Record<string, number> = {
                all: 0,
                new: 0,
                confirmed: 0,
                pending: 0,
                picked: 0,
                cancel: 0,
            };

            res.data.forEach((item) => {
                const key = statusKeyMap[String(item.status)];
                if (!key) return;
                next[key] = Number(item.count) || 0;
            });

            setStatusSummary(next);
        } catch {
            // ignore summary errors to avoid blocking page
        }
    }, [selectedTelesaleId, debouncedKeyword, datePeriod]);

    useEffect(() => {
        fetchStatusSummary();
    }, [fetchStatusSummary]);

    // Tải danh sách nhân viên telesale (người phụ trách) cho bộ lọc
    const fetchTelesaleOptions = useCallback(async () => {
        try {
            const res = await mixGetV1('/api/v1/be/staffs', {
                type_user: 1,
                type_department_id: 3,
                sort: 'id,desc',
                status: 0,
                take: 200,
            });
            const rawItems = Array.isArray(res?.data?.items)
                ? res.data.items
                : Array.isArray(res?.data) ? res.data : [];
            setTelesaleOptions(
                rawItems.filter((s: any) => s?.id).map((s: any) => ({
                    label: s?.fullname || s?.name || `#${s.id}`,
                    value: Number(s.id),
                }))
            );
        } catch {
            // ignore
        }
    }, []);

    useEffect(() => {
        fetchTelesaleOptions();
    }, [fetchTelesaleOptions]);

    const handleUploadClick = () => {
        fileInputRef.current?.click();
    };

    const viewMenu = [
        {
            key: "table",
            label: "Table view",
            icon: <Table2 size={14} />,
        },
        {
            key: "pipeline",
            label: "Pipeline view",
            icon: <LayoutGrid size={14} />,
        },
    ];

    const fetchStaffOptions = useCallback(async () => {
        if (!isAdmin) return;
        setLoadingStaffOptions(true);
        try {
            const res = await mixGetV1('/api/v1/be/staffs', {
                type_user: 1,
                type_department_id: 2,
                sort: 'id,desc',
                status: 0,
                take: 200,
            });

            if (res?.status) {
                const rawItems = Array.isArray(res?.data?.items)
                    ? res.data.items
                    : Array.isArray(res?.data)
                        ? res.data
                        : [];

                const nextOptions = rawItems
                    .filter((s: any) => s?.id)
                    .map((s: any) => ({
                        label: s?.fullname || s?.name || `#${s.id}`,
                        value: Number(s.id),
                    }));

                setStaffOptions(nextOptions);
            }
        } catch (error) {
            message.error('Không tải được danh sách nhân viên trực page');
        } finally {
            setLoadingStaffOptions(false);
        }
    }, [isAdmin]);

    useEffect(() => {
        fetchStaffOptions();
    }, [fetchStaffOptions]);

    const handleChangeStaff = async (staffId: string | number, record: AppointmentItem) => {
        if (!isAdmin) {
            message.error('Bạn không có quyền phân công nhân viên trực page');
            return;
        }

        const staffIdNum = Number(staffId);
        const selected = staffOptions.find((s) => s.value === staffIdNum);
        const prevStaff = record.staff;

        setData((prev) =>
            prev.map((item) =>
                item.id === record.id
                    ? {
                        ...item,
                        staff: {
                            id: staffIdNum,
                            fullname: selected?.label || "",
                            name: selected?.label || "",
                        },
                    }
                    : item
            )
        );

        setUpdatingStaffIds((prev) => [...prev, record.id]);

        try {
            const res = await mixPutV1(`/api/v1/be/appointments/${record.id}`, {
                staff_id: staffIdNum,
                channel_customer_id: record.channel_customer_id,
            });

            if (!res?.status) {
                throw new Error(res?.msg || 'Update failed');
            }
            message.success('Cập nhật nhân viên trực page thành công');
        } catch (error) {
            setData((prev) =>
                prev.map((item) =>
                    item.id === record.id
                        ? {
                            ...item,
                            staff: prevStaff || null,
                        }
                        : item
                )
            );
            message.error('Cập nhật nhân viên trực page thất bại');
        } finally {
            setUpdatingStaffIds((prev) => prev.filter((id) => id !== record.id));
        }
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();

        reader.onload = (evt) => {
            const data = evt.target?.result;
            const workbook = XLSX.read(data, { type: "binary" });

            const sheetName = workbook.SheetNames[0];
            const sheet = workbook.Sheets[sheetName];

            const jsonData = XLSX.utils.sheet_to_json(sheet);

            console.log("Excel data:", jsonData);
            message.success(t('order.import_success'));

            // TODO: push vào table
        };

        reader.readAsBinaryString(file);
    };

    const handleExportExcel = () => {

        const dataExport = data.map((item) => ({
            [t('order.col_order_code')]: item.code,
            [t('order.col_customer')]: item.customer_name || item.customer?.display_name || '',
            [t('create_order.service')]: (item as any).services?.length > 0
                ? (item as any).services.map((s: any) => s.name).join(', ')
                : (item.service?.name || ''),
            [t('order.col_assigned_staff')]: item.staff?.fullname || '',
            'Ngày hẹn': item.scheduled_at || '',
            [t('order.col_note')]: item.note || '',
            [t('order.col_status')]: item.customer_tag_status_text || '',
        }));

        const worksheet = XLSX.utils.json_to_sheet(dataExport);

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Orders");

        XLSX.writeFile(workbook, "orders.xlsx");
        message.success(t('order.export_success'));
    };

    const colorMap: Record<string, string> = {
        gold: "bg-[#3e91ab]",
        blue: "bg-blue-500",
        orange: "bg-orange-500",
        cyan: "bg-cyan-500",
        red: "bg-red-500",
        green: "bg-green-500",
    };

    const borderMap: Record<string, string> = {
        gold: "border-[#3e91ab]",
        blue: "border-blue-500",
        orange: "border-orange-500",
        cyan: "border-cyan-500",
        red: "border-red-500",
        green: "border-green-500",
    };

    const tabs = [
        { key: "all", label: t('order.all'), count: statusSummary.all, color: "gold" },
        { key: "new", label: t('order.new'), count: statusSummary.new, color: "blue" },
        { key: "confirmed", label: t('order.confirmed'), count: statusSummary.confirmed, color: "cyan" },
        { key: "pending", label: t('order.pending_pickup'), count: statusSummary.pending, color: "orange" },
        { key: "picked", label: t('order.picked_up'), count: statusSummary.picked, color: "green" },
        { key: "cancel", label: t('order.cancel'), count: statusSummary.cancel, color: "red" },
    ];

    const statusLabels: Record<number, { label: string; color: string }> = {
        1: { label: t('order.new'), color: 'blue' },
        2: { label: t('order.confirmed'), color: 'cyan' },
        3: { label: t('order.pending_pickup'), color: 'orange' },
        4: { label: t('order.picked_up'), color: 'green' },
        0: { label: t('order.cancel'), color: 'red' },
    };

    const getMenuItems = (recordId: number) => [
        // {
        //     key: "print",
        //     label: (
        //         <span>
        //             <PrinterOutlined /> {t('order.print')}
        //         </span>
        //     ),
        // },
        {
            key: "edit",
            label: (
                <span onClick={() => navigate(`/appointment/detail/${recordId}`)}>
                    <EditOutlined /> {t('order.edit_order')}
                </span>
            ),
        },
        // { type: "divider" as const },
        // { key: "success", label: t('order.delivery_success') },
        // { key: "shipping", label: t('order.shipping') },
        // { key: "cancel", label: t('order.cancel') },
        // { key: "done", label: t('order.done') },
    ];

    // Cột được đặt width cố định + ellipsis để dữ liệu dài không kéo bảng rộng ra;
    // cột khách hàng ghim trái làm mốc, cột thao tác ghim phải để luôn thấy được
    // mà không phải kéo ngang.
    const columns = [
        {
            key: 'customer',
            title: t('order.col_customer'),
            width: 190,
            fixed: 'left' as const,
            render: (_: any, record: AppointmentItem) => {
                const name = record.customer_name || record.customer?.display_name || '';
                return (
                    <div className="min-w-0">
                        <div
                            onClick={() => navigate(`/appointment/detail/${record.id}`)}
                            className="text-[#3e91ab] font-medium cursor-pointer truncate"
                            title={name}
                        >
                            {name || '—'}
                        </div>
                        {record?.customer_tag?.name && (
                            <Tag
                                color={record.customer_tag.color || undefined}
                                className="mt-1 max-w-full truncate"
                                title={record.customer_tag.name}
                            >
                                {record.customer_tag.name}
                            </Tag>
                        )}
                    </div>
                );
            },
        },
        /* ----- Các cột theo "Frontend Column Mapping" của API /appointments -----
           Cột ít dùng để defaultHidden: người dùng tự bật trong cài đặt cột. */
        {
            key: 'code',
            title: t('order.col_order_code'),
            width: 120,
            render: (_: any, record: AppointmentItem) => record.code ? (
                <span
                    onClick={() => navigate(`/appointment/detail/${record.id}`)}
                    className="font-semibold text-[#3e91ab] cursor-pointer whitespace-nowrap"
                >
                    {record.code}
                </span>
            ) : '—',
        },
        {
            key: 'customer_phone',
            title: t('customer.col_phone'),
            width: 130,
            render: (_: any, record: AppointmentItem) => record.customer_phone
                ? <a href={`tel:${record.customer_phone}`} className="whitespace-nowrap">{record.customer_phone}</a>
                : '—',
        },
        {
            key: 'customer_email',
            title: 'Email',
            width: 180,
            ellipsis: true,
            defaultHidden: true,
            render: (_: any, record: AppointmentItem) => <span title={record.customer_email || ''}>{record.customer_email || '—'}</span>,
        },
        {
            title: 'Ngày hẹn',
            dataIndex: "scheduled_at",
            width: 130,
            render: (text: string) => <ScheduleDateCell value={text} />,
        },
        {
            key: 'service',
            title: t('create_order.service'),
            width: 180,
            render: (_: any, record: any) => {
                const services = record.services;
                if (services && services.length > 0) {
                    return (
                        <div className="max-h-[72px] overflow-y-auto">
                            {services.map((s: any, i: number) => (
                                <div key={s.id || i} className="truncate text-sm leading-5" title={s.name}>
                                    {s.name}
                                </div>
                            ))}
                        </div>
                    );
                }
                return (
                    <div className="truncate" title={record.service?.name || '—'}>
                        {record.service?.name || '—'}
                    </div>
                );
            },
        },
        {
            title: t('order.col_branch'),
            dataIndex: "branch",
            width: 130,
            ellipsis: true,
            render: (_: any, record: AppointmentItem) => (
                <span title={record.branch?.name || ''}>{record.branch?.name || '—'}</span>
            ),
        },
        {
            key: 'telesale',
            title: 'Telesale',
            width: 130,
            ellipsis: true,
            render: (_: any, record: AppointmentItem) => {
                const name = record.telesale?.name || record.telesale?.fullname || '';
                return <span title={name}>{name || '—'}</span>;
            },
        },
        {
            key: 'staff',
            title: t('customer.page_staff'),
            width: 150,
            ellipsis: true,
            defaultHidden: true,
            render: (_: any, record: AppointmentItem) => {
                const name = record.staff?.name || '';
                return <span title={name}>{name || '—'}</span>;
            },
        },
        {
            key: 'branch_staff',
            title: t('order.col_branch_staff'),
            width: 150,
            ellipsis: true,
            defaultHidden: true,
            render: (_: any, record: AppointmentItem) => {
                const name = record.branch_staff?.name || record.branch_staff?.fullname || '';
                return <span title={name}>{name || '—'}</span>;
            },
        },
        {
            key: 'action_staff',
            title: t('order.col_action_staff'),
            width: 150,
            ellipsis: true,
            defaultHidden: true,
            render: (_: any, record: AppointmentItem) => {
                const name = record.action_staff?.name || record.action_staff?.fullname || '';
                return <span title={name}>{name || '—'}</span>;
            },
        },
        {
            key: 'status',
            title: t('order.col_status'),
            width: 120,
            render: (_: any, record: AppointmentItem) => {
                const tag = {
                    status: record.customer_tag_status,
                    status_text: record.customer_tag_status_text,
                };
                if (!tag) return <span>—</span>;
                const color = tag.status != null ? (statusLabels[tag.status]?.color || undefined) : undefined;
                const label = tag.status_text || '—';
                return (
                    <Tag color={color} className="max-w-full truncate" title={label}>
                        {label}
                    </Tag>
                );
            },
        },
        {
            key: 'customer_tag',
            title: t('order.col_customer_tag'),
            width: 170,
            defaultHidden: true,
            render: (_: any, record: AppointmentItem) => record.customer_tag?.name ? (
                <Tag color={record.customer_tag.color || undefined} className="max-w-full truncate" title={record.customer_tag.name}>
                    {record.customer_tag.name}
                </Tag>
            ) : '—',
        },
        {
            key: 'care_status',
            title: t('order.col_care_status'),
            width: 130,
            defaultHidden: true,
            render: (_: any, record: AppointmentItem) => {
                if (record.is_cared === undefined && !record.care_status_text) return '—';
                const label = record.care_status_text || (record.is_cared ? 'Đã chăm sóc' : 'Chưa chăm sóc');
                return <Tag color={record.is_cared ? 'green' : 'default'} className="mr-0">{label}</Tag>;
            },
        },
        {
            key: 'customer_gender',
            title: t('customer.col_gender'),
            width: 100,
            defaultHidden: true,
            render: (_: any, record: AppointmentItem) => {
                const map: Record<number, string> = {
                    0: t('customer.gender_female'),
                    1: t('customer.gender_male'),
                    2: t('customer.gender_other'),
                };
                const g = record.customer_gender;
                return g !== null && g !== undefined && map[g] ? map[g] : '—';
            },
        },
        {
            key: 'customer_dob',
            title: t('customer.col_birthday'),
            width: 150,
            defaultHidden: true,
            render: (_: any, record: AppointmentItem) => {
                const d = record.customer_dob ? dayjs(record.customer_dob) : null;
                if (!d || !d.isValid()) return '—';
                return (
                    <span className="whitespace-nowrap">
                        {d.format('DD/MM/YYYY')}
                        <span className="text-gray-400 ml-1">({dayjs().diff(d, 'year')} {t('customer.age_suffix')})</span>
                    </span>
                );
            },
        },
        {
            key: 'is_foreign',
            title: t('customer.col_origin'),
            // Đủ chỗ cho tag "Nước ngoài / Việt kiều" (bảng dùng layout fixed nên hẹp là bị cắt)
            width: 180,
            defaultHidden: true,
            render: (_: any, record: AppointmentItem) => record.is_foreign === 1
                ? <Tag color="purple" className="mr-0">{t('customer.origin_foreign')}</Tag>
                : t('customer.origin_domestic'),
        },
        {
            key: 'address',
            title: t('customer.col_address'),
            width: 160,
            ellipsis: true,
            defaultHidden: true,
            render: (_: any, record: AppointmentItem) => <span title={record.address || ''}>{record.address || '—'}</span>,
        },
        {
            key: 'ward',
            title: t('customer.col_ward'),
            width: 130,
            ellipsis: true,
            defaultHidden: true,
            render: (_: any, record: AppointmentItem) => {
                const name = record.ward?.name || record.ward?.full_name || '';
                return <span title={record.ward?.full_name || name}>{name || '—'}</span>;
            },
        },
        {
            key: 'province',
            title: t('customer.col_province'),
            width: 140,
            ellipsis: true,
            defaultHidden: true,
            render: (_: any, record: AppointmentItem) => {
                const name = record.province?.name || record.province?.full_name || '';
                return <span title={record.province?.full_name || name}>{name || '—'}</span>;
            },
        },
        {
            title: t('order.col_note'),
            dataIndex: "note",
            width: 160,
            ellipsis: true,
            render: (text: string) => (
                <span className="text-gray-500 dark:text-gray-400" title={text || ''}>
                    {text || '—'}
                </span>
            ),
        },
        {
            key: 'created_by',
            title: t('order.col_created_by'),
            width: 150,
            render: (_: any, record: AppointmentItem) => (
                <div className="min-w-0">
                    <div className="truncate" title={record.creator?.fullname || ''}>
                        {record.creator?.fullname || '—'}
                    </div>
                    <div className="text-xs text-gray-500 truncate">
                        {record.created_at ? new Date(record.created_at).toLocaleString('vi-VN') : ''}
                    </div>
                </div>
            ),
        },
        {
            key: 'updated_at',
            title: t('customer.col_updated_at'),
            width: 150,
            defaultHidden: true,
            render: (_: any, record: AppointmentItem) => record.updated_at && dayjs(record.updated_at).isValid()
                ? <span className="whitespace-nowrap">{dayjs(record.updated_at).format('DD/MM/YYYY HH:mm')}</span>
                : '—',
        },
        {
            title: '',
            settingLabel: t('order.col_actions'),
            lockVisible: true,
            key: 'actions',
            width: 56,
            align: 'center' as const,
            fixed: 'right' as const,
            render: (_: any, record: AppointmentItem) => (
                <Dropdown menu={{ items: getMenuItems(record.id) }} trigger={["click"]}>
                    <Button type="text" icon={<MoreOutlined />} />
                </Dropdown>
            ),
        },
    ];

    // Ẩn/hiện, sắp xếp, cố định, độ rộng cột — lưu theo tài khoản (extra_data)
    const {
        columns: tableColumns,
        components: tableComponents,
        tableLayout,
        scrollX,
        settingProps: columnSettingProps,
    } = useTableColumnSettings("appointment_list", columns);
    // Chưa chỉnh độ rộng: scroll.x = tổng độ rộng các cột đang hiện (cột đều khai báo width)
    const tableScrollX = tableLayout === 'fixed'
        ? scrollX
        : tableColumns.reduce((sum, c) => sum + (Number(c.width) || 150), 0);

    return (
        <Layout>
            <Helmet>
                <title>{t('page.orders')} | Khothi</title>
            </Helmet>
            <div className="space-y-4">

                {/* Title */}
                <h2 className="text-lg font-semibold text-000000e0">{t('order.title')}</h2>

                {/* Tabs */}
                <div className="flex overflow-x-auto scrollbar-hide gap-2">
                    {tabs.map((tab) => {
                        const active = activeTab === tab.key;

                        const bgColor = tab.color ? colorMap[tab.color] : "bg-[#3e91ab]";
                        const borderColor = tab.color ? borderMap[tab.color] : "border-[#297288]";

                        return (
                            <button
                                key={tab.key}
                                onClick={() => setActiveTab(tab.key)}
                                className={`
                                    flex items-center gap-2 px-3 py-1 text-sm rounded border text-000000e0
                                    transition
                                    ${active ? `${bgColor} !text-white ${borderColor}` : "bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700"}
                                `}
                            >
                                <span className="whitespace-nowrap">
                                    {tab.label}
                                </span>

                                <span
                                    className={`px-1.5 text-xs rounded text-white ${bgColor}`}
                                >
                                    {tab.count}
                                </span>
                            </button>
                        );
                    })}
                </div>

                {/* Khối biểu đồ thống kê (chỉ super admin): tạm ẩn theo yêu cầu, bật lại bằng cách bỏ comment */}
                {/* {isSuperAdmin && <ChartBlock />} */}

                {/* Toolbar */}
                <div className="flex flex-col xl:flex-row justify-between gap-3 w-full toolbar-customer">

                    {/* KHU VỰC 1: TÌM KIẾM & LỌC (Dòng 1 & 2 trên Mobile) */}
                    <div className="flex flex-col md:flex-row gap-3 w-full xl:w-auto">
                        {/* Dòng 1 (Mobile): Search Input */}
                        <Input
                            placeholder="Mã lịch hẹn, tên khách hàng, SĐT..."
                            prefix={<SearchOutlined className="text-gray-400" />}
                            className="w-full md:w-[320px] h-10 dark:bg-[#1f1f1f] dark:border-[#303030] dark:text-white"
                            value={keyword}
                            onChange={(e) => setKeyword(e.target.value)}
                            allowClear
                        />

                        {/* Dòng 2 (Mobile): Grid 2 cột cho 2 Select */}
                        <div className="grid grid-cols-2 gap-2 md:gap-3 w-full md:w-auto">

                            {/* Người phụ trách */}
                            <div className="flex items-center px-2 border border-[#d9d9d9] rounded-md bg-white dark:bg-[#1f1f1f] dark:border-[#303030] w-full md:w-[220px] h-10 overflow-hidden">
                                {/* Thêm shrink-0 để icon không bị bóp méo, giảm mr-2 xuống mr-1.5 để tiết kiệm không gian */}
                                <UserOutlined className="text-gray-400 mr-1.5 shrink-0" />
                                <Select
                                    bordered={false}
                                    placeholder={t('customer.select_responsible')}
                                    // Thêm min-w-0 và ép padding của Antd nhỏ lại bằng arbitrary variants của Tailwind
                                    className="w-full flex-1 min-w-0 [&_.ant-select-selector]:!px-0"
                                    allowClear
                                    showSearch
                                    optionFilterProp="label"
                                    value={selectedTelesaleId}
                                    onChange={(val) => setSelectedTelesaleId(val ?? undefined)}
                                    options={telesaleOptions}
                                    popupMatchSelectWidth={false}
                                    dropdownMatchSelectWidth={false}
                                />
                            </div>

                            {/* Thời gian */}
                            <div className="flex items-center px-2 border border-[#d9d9d9] rounded-md bg-white dark:bg-[#1f1f1f] dark:border-[#303030] w-full md:w-[180px] h-10 overflow-hidden">
                                <ClockCircleOutlined className="text-gray-400 mr-1.5 shrink-0" />
                                <Select
                                    bordered={false}
                                    className="w-full flex-1 min-w-0 [&_.ant-select-selector]:!px-0"
                                    placeholder={t('order.by_scheduled_date')}
                                    allowClear
                                    value={datePeriod}
                                    onChange={(val) => setDatePeriod(val ?? undefined)}
                                    options={[
                                        { label: t('order.scheduled_yesterday'), value: 'yesterday' },
                                        { label: t('order.scheduled_today'), value: 'date' },
                                        { label: t('order.scheduled_tomorrow'), value: 'tomorrow' },
                                        { label: t('order.scheduled_this_week'), value: 'week' },
                                        { label: t('order.scheduled_this_month'), value: 'month' },
                                        { label: t('order.scheduled_this_year'), value: 'year' },
                                    ]}
                                />
                            </div>
                        </div>
                    </div>

                    {/* KHU VỰC 2: CÁC NÚT THAO TÁC (Dòng 3 trên Mobile) */}
                    <div className="flex flex-row items-center gap-2 w-full xl:w-auto">
                        <input
                            type="file"
                            ref={fileInputRef}
                            onChange={handleFileChange}
                            accept=".xlsx,.xls"
                            hidden
                        />

                        {/* Nút View: hiện thẳng 2 lựa chọn, không gom vào dropdown */}
                        {viewMenu.map((item) => (
                            <Button
                                key={item.key}
                                onClick={() => handleViewChange(item.key)}
                                icon={item.icon}
                                className={`h-10 flex items-center justify-center shrink-0 ${view === item.key
                                    ? "!bg-[#3e91ab] !text-white !border-[#3e91ab]"
                                    : "border-[#d9d9d9]"
                                    }`}
                                title={item.label}
                            >
                                <span className="hidden sm:inline">{item.label}</span>
                            </Button>
                        ))}

                        {/* Nút Upload: Chỉ hiện icon trên điện thoại, giữ lại text trên PC */}
                        <Button
                            icon={<UploadOutlined />}
                            onClick={handleUploadClick}
                            className="flex items-center justify-center shrink-0 h-10 border-[#d9d9d9]"
                            title="Upload Excel"
                        >
                            <span className="hidden sm:inline">Upload</span>
                        </Button>

                        {/* Nút Export: Tương tự Upload */}
                        <Button
                            icon={<FileExcelOutlined />}
                            onClick={handleExportExcel}
                            className="flex items-center justify-center shrink-0 h-10 border-[#d9d9d9]"
                            title={t('order.export_orders')}
                        >
                            <span className="hidden sm:inline">Export</span>
                        </Button>

                        {view === "table" && (
                            <ColumnSettingButton {...columnSettingProps} className="!h-10 !w-10" />
                        )}

                        {/* Nút Thêm mới: Nằm bên phải cùng, chiếm nốt không gian còn lại (flex-1) */}
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={() => navigate("/appointment/create")}
                            className="bg-[#3e91ab] hover:!bg-[#287891] border-none flex items-center justify-center flex-1 sm:flex-none h-10 shadow-none font-medium"
                        >
                            Đặt lịch mới
                        </Button>
                    </div>
                </div>

                {/* Table */}
                <Spin spinning={loading}>
                    <div className="bg-white dark:bg-gray-900 rounded-lg shadow-sm">
                        {view === "table" ? (
                            <Table
                                columns={tableColumns}
                                components={tableComponents}
                                tableLayout={tableLayout}
                                dataSource={data}
                                rowKey="id"
                                pagination={{
                                    current: pagination.current,
                                    total: pagination.total,
                                    pageSize: pagination.pageSize,
                                    onChange: (p) => fetchData(p),
                                }}
                                scroll={{ x: tableScrollX }}
                                size="middle"
                                className="custom-ant-table appointment-table"
                            />
                        ) : (
                            <Calendar
                                filters={{
                                    search: debouncedKeyword || undefined,
                                    status: statusMap[activeTab],
                                    telesale_id: selectedTelesaleId,
                                }}
                                scheduledPeriod={datePeriod}
                            />
                        )}
                    </div>
                </Spin>
            </div>
        </Layout>
    );
}